import type { IFilterOption } from "../filters/types";

export type { TModelCapabilityLabelKey } from "../constants";
export { EModelCapabilityKey } from "./model-capability";

export enum EModelProvider {
  OPENAI = "openai",
  GOOGLE = "gemini",
  MISTRAL = "mistral",
  ANTHROPIC = "claude",
  OLLAMA = "ollama",
  QWEN = "qwen",
  GROK = "grok",
  DEEPSEEK = "deepseek",
  // NOTE: NSAI-5056 — Moonshot (Kimi) and Z.ai (GLM) are reached over an
  // OpenAI-compatible protocol, which is why their rows used to carry
  // provider "openai". That put a TRANSPORT fact in a VENDOR field: this enum
  // is what MODEL_PROVIDER_LABELS, the create-model form and the identity
  // prompt all read as "who made this model". Naming them honestly is what
  // lets icons, labels and prompts key off the provider instead of guessing
  // the vendor from the model name.
  MOONSHOT = "moonshot",
  ZAI = "zai",
  SELFHOSTED = "selfhosted",
}

// NOTE: NSAI-6000 — CHINA is where the DeepSeek platform rows actually run
// (migration 20260910103755_host_deepseek_models_in_china). It sits with the
// other named regions rather than at the end: GLOBAL is the catch-all for a row
// that is not pinned anywhere, not a region of its own. The order here is the
// order REGION_OPTIONS offers in the create-model form.
export enum EModelRegion {
  US = "US",
  EU = "EU",
  UAE = "UAE",
  CHINA = "China",
  GLOBAL = "Global",
}

// NOTE: NSAI-6000 — DEEPSEEK is the vendor hosting its own models, the same
// shape of value as FIREWORKS.
// NOTE: NSAI-6145 — OPENAI, ANTHROPIC, MISTRAL and GROK join them: the catalog
// moved 62 gateway-routed rows off AZURE / GCP-era values onto the vendor that
// actually serves them, so these are seeded hosting providers an admin must also
// be able to pick for a custom model (the enum gates the create-model form via
// HOSTING_PROVIDER_OPTIONS and CreateModelDto's @IsEnum — display paths fall
// back to the raw string and never needed it). This enum is still not exhaustive
// over the seeded catalog ("Google", "LLM360" and "Ollama" are hosting providers
// with no member here); closing that wider gap is its own change.
export enum EModelHostingProvider {
  AWS = "AWS",
  GCP = "GCP",
  AZURE = "Azure",
  NODESHIFT = "NodeShift",
  COMPASS = "Compass",
  FIREWORKS = "Fireworks",
  DEEPSEEK = "DeepSeek",
  OPENAI = "OpenAI",
  ANTHROPIC = "Anthropic",
  MISTRAL = "Mistral",
  GROK = "Grok",
  SELFHOSTED = "SelfHosted",
}

export interface IBaseModelFields {
  name: string;
  title: string | null;
  // NOTE: NSAI-4494 — optional short blurb ("a few words on what the model is
  // good at") shown in model dropdowns. Null/absent when the model has none.
  tagline?: string | null;
  version: string;
  provider: EModelProvider;
  quality: number;
  speed: number;
  isDocsSupported: boolean;
  isImagesSupported: boolean;
  isToolSupported: boolean;
  isReasoningModel: boolean;
  isHighEffortOnlyModel: boolean;
  isCanvasSupported: boolean;
  // NOTE: NSAI-7217 — the model is given the ```ui contract (Intelligent UI).
  // Server-managed: set per catalog row by a migration once the model passes
  // its evaluation, never through the create / update DTOs.
  isInteractiveUiSupported: boolean;
  isImageGenerationModel?: boolean;
  // NOTE: NSAI-6621 — a text/image-to-video model (e.g. Veo). Not a chat
  // model: excluded wherever image models are, and a turn on it renders a clip.
  isVideoGenerationModel?: boolean;
  isGuardModel?: boolean;
  // NOTE: Marks a text-embedding model (not a chat model). Embedding models are
  // excluded from chat model lists and are selectable per-org for semantic
  // search. `embeddingDimensions` is the model's output vector size, set only
  // for embedding models.
  isEmbeddingModel?: boolean;
  embeddingDimensions?: number | null;
  // NOTE: NSAI-4948 (contract C1) — marks the Auto router model: a catalogue row
  // that picks a concrete model per request instead of answering itself. The
  // picker labels it "Auto" and never combines it with other models or
  // assistants.
  isRouterModel?: boolean;
  region: EModelRegion;
  hostingProvider: EModelHostingProvider;
  baseUrl: string | null;
  contextSize: number;
  maxTokens: number;
  maxMessagesPerUser: number;
  isMaintenanceMode: boolean;
}

export interface IModelOwner {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  // NOTE: NSAI-7736 — the IdP login name of an owner with no email; absent
  // from a server that predates it.
  username?: string | null;
}

// NOTE: NSAI-6657 — USD per 1,000,000 tokens, the catalog rates the Token &
// Cost Usage screen bills with. GET /models and the organization's models
// projection have always serialized them (both spread the entity in the
// server's model parsers); the Models tables read them. Optional for a server
// that predates the columns; null = unpriced. Kept out of IBaseModelFields,
// which the create / update DTOs build on: a price is not set from the UI.
export interface IModelCatalogPrices {
  inputTokenPricePerMillion?: number | null;
  outputTokenPricePerMillion?: number | null;
}

export interface IModel extends IBaseModelFields, IModelCatalogPrices {
  uuid: string;
  ownerType: "app" | "organization" | "project";
  // NOTE: NSAI-4359 — `enabled` is the resolved PER-PROJECT visibility of the
  // model in the current project (not a user preference). A model with no
  // project_model_visibility row resolves to enabled=true. The dead
  // isOwner/isEnabledByOwner fields (never serialized by the server) were
  // removed here.
  enabled: boolean;
  isWebSearchEnabled: boolean;
  // NOTE: NSAI-4241 — the server never serializes the raw provider `apiKey`; it
  // sends this boolean so the UI can show whether a key is configured.
  hasApiKey: boolean;
  createdAt: string;
  updatedAt: string;
  owner?: IModelOwner | null;
}

// NOTE: isCanvasSupported and isInteractiveUiSupported are server-managed:
// the server's CreateModelDto has neither, and its whitelist rejects them.
export interface ICreateModelDto extends Partial<
  Omit<IBaseModelFields, "isCanvasSupported" | "isInteractiveUiSupported">
> {
  name: string;
  provider: EModelProvider;
  quality: number;
  speed: number;
  // NOTE: NSAI-4241 — `apiKey` is an outbound create/update field (it lives on
  // the request, never the response), so it stays on the DTO even though it was
  // removed from the shared read model IBaseModelFields.
  apiKey?: string | null;
}

// NOTE: NSAI-4359 — body for PATCH /models/:uuid (project models) and, via the
// org-model route, PATCH org-models. Partial update: every field optional.
// region / provider / hostingProvider / name are ABSENT because they are baked
// into the immutable model key; isCanvasSupported and isInteractiveUiSupported
// are server-managed and not accepted by the update DTO. apiKey stays
// (outbound-only, like ICreateModelDto).
export interface IUpdateModelDto extends Partial<
  Omit<
    IBaseModelFields,
    | "name"
    | "provider"
    | "hostingProvider"
    | "region"
    | "isCanvasSupported"
    | "isInteractiveUiSupported"
  >
> {
  apiKey?: string | null;
}

export interface IUpdateModelParams {
  uuid: string;
  data: IUpdateModelDto;
}

// NOTE: Minimal shape returned by GET /models/embedding-eligible — embedding
// models whose vector dimension matches the active search index. Used to
// populate the org search-settings embedding-model dropdown.
export interface IEmbeddingModelOption {
  uuid: string;
  name: string;
  title: string;
  provider: EModelProvider;
  hostingProvider: EModelHostingProvider;
  embeddingDimensions: number | null;
}

export interface IToggleModelEnabledProps {
  uuid: string;
  enabled: boolean;
}

export interface IModelSettings {
  defaultModel: string | null;
  lastUsedModelOrAssistant: string | null;
  defaultImageModel: string | null;
  defaultSummaryModel: string | null;
  // NOTE: NSAI-5786 — deployment-wide new-chat model override (a bare model
  // `name`), served read-only by the server. Not a user preference: it is never
  // written back, and `setModelSettings` rejects it.
  forcedNewChatModelName: string | null;
  // NOTE: NSAI-6445 — the model a summary runs on while `defaultSummaryModel`
  // is unset: the organization's Chat Settings Summary model, else the
  // deployment default. Resolved and served read-only by the server (the same
  // resolver the summary itself runs on), so the Models page can name it
  // without guessing. Null when the deployment cannot resolve one either.
  summaryFallbackModel: ISummaryFallbackModel | null;
}

export interface ISummaryFallbackModel {
  uuid: string;
  title: string;
}

// NOTE: NSAI-5786 — the IModelSettings keys a caller may actually write.
// `forcedNewChatModelName` is deployment config the server serves read-only, so
// excluding it here makes `setModelSettings("forcedNewChatModelName", …)` a
// compile error instead of a silent no-op against an unmapped server key.
export type TWritableModelSetting = Exclude<
  keyof IModelSettings,
  "forcedNewChatModelName" | "summaryFallbackModel"
>;

// NOTE: NSAI-4838 — the server-side per-(user, project) model defaults. Only the
// three DEFAULTS are persisted (last-used-model stays device-local). Mirrors the
// GET/PATCH /models/settings payload. A PATCH sends a partial: an omitted field
// is unchanged, an explicit null clears it.
export interface IModelSettingsPayload {
  defaultModelUuid: string | null;
  defaultSummaryModelUuid: string | null;
  defaultImageModelUuid: string | null;
}

// NOTE: NSAI-5786 — what GET/PATCH /models/settings actually return: the three
// PATCHable defaults above plus one read-only, deployment-wide field. Kept as a
// separate interface rather than widening IModelSettingsPayload, so the PATCH
// body type still cannot name a field the server would reject.
export interface IModelSettingsResponse extends IModelSettingsPayload {
  forcedNewChatModelName: string | null;
  // NOTE: NSAI-6445 — read-only, see IModelSettings.summaryFallbackModel.
  summaryFallbackModel: ISummaryFallbackModel | null;
}

export interface IModelUsage {
  assistantCount: number;
  cronJobCount: number;
  webhookCount: number;
  hookCount: number;
  workflowCount: number;
}

export interface IToggleModelEnabledResponse {
  message: string;
  usage: IModelUsage;
  // NOTE: NSAI-7377 / NSAI-7378 — server-localized consequences of hiding the
  // model for the project (chats locked to it by PII Intent Routing, the org's
  // PII trusted / classifier model hidden here). Clients show each as a
  // warning toast. Omitted when there is nothing to say.
  warnings?: string[];
  // NOTE: Informational lines that are not consequences (a member hiding the
  // org's probe model does not change moderation). Clients show them as an
  // info toast. Omitted when there is nothing to say.
  notices?: string[];
}

export interface ICapabilityFilterTarget {
  isImageGenerationModel?: boolean;
  isVideoGenerationModel?: boolean;
  isImagesSupported: boolean;
  isDocsSupported: boolean;
  isToolSupported: boolean;
}

// NOTE: Minimum fields needed to build provider-grouped filter options.
// `providerLabel` is added by each platform's UI-model parser (web:
// pages/chat/ModelPicker; mobile: parsers/modelParser) on top of IModel.
export type TModelFilterInput = Pick<IModel, "name" | "title" | "provider"> &
  Partial<Pick<IModel, "isRouterModel">> & {
    providerLabel: string;
  };

export interface IModelFilterOptions {
  modelOptions: IFilterOption[];
  providerOptions: IFilterOption[];
  // NOTE: Maps each provider option's `value` (the provider display label) to the
  // distinct underlying provider enums in that group. NodeShift-hosted models all
  // collapse under "On-Prem AI Model" but carry different `provider` enums; selecting
  // the chip must filter messages by every enum, not just the first model's.
  providerLabelToProviderNames: Record<string, string[]>;
}

export interface ITranslateModelFilterOptionsProps {
  options: IModelFilterOptions;
  resolveProviderLabel: (providerLabel: string) => string;
}

export interface IGroupedModelEntry<T extends TModelFilterInput> {
  models: T[];
}

export interface IHasDuplicateModelTitleParams {
  title: string;
  // NOTE: The check only reads `title`, so it accepts any model carrying the
  // base fields — project (IModel) and organization-owned models alike.
  models: Pick<IBaseModelFields, "title">[];
}

export interface IVerifyApiKeyDto {
  provider: EModelProvider;
  hostingProvider: EModelHostingProvider;
  apiKey: string;
  baseUrl: string;
}

export interface IVerifyApiKeyResponse {
  valid: boolean;
}
